'use client';

import { cn } from 'cn';

import { ProjectResponseAudio } from '@/shared-survey';
import { getAlphabetLabel } from '@/shared-utilities';

import { ProjectResponseAssetItem } from './project-response-asset-item';
import ProjectResponseMessageRendererAudio from './project-response-message-renderer-audio';
import { ProjectResponseQuestionText } from './project-response-question-text';
import { ProjectResponseComponentProps } from './project-response-renderer.type';
import ProjectResponseSelectItem from './project-response-select-item';
import {
  useReorderOtherOptionToLast,
  useProjectResponseSelect,
} from './project-response-select.hooks';

interface ProjectResponseSelectProps {
  options: {
    label: string;
    value: string;
  }[];
  value: string;
  tentativeValue?: string;
  onClick: (value: string) => void;
  readonly?: boolean;
  assets?: ProjectResponseComponentProps['assets'];
  text?: string;
  audio?: ProjectResponseAudio;
  maxSelections?: number;
  isAiVoiceEnabled?: boolean;
  app?: 'manager' | 'survey';
  className?: string;
}

export default function ProjectResponseSelect({
  app,
  options,
  value,
  tentativeValue,
  onClick,
  readonly,
  assets,
  text,
  audio,
  maxSelections,
  isAiVoiceEnabled,
  className,
}: ProjectResponseSelectProps) {
  // Reorder options so that 'other' option is always last
  const reorderedOptions = useReorderOtherOptionToLast(options);

  // Use hook for selection logic
  const { isValueSelected, handleClick } = useProjectResponseSelect(
    value,
    onClick,
    maxSelections,
    tentativeValue
  );

  return (
    <div
      data-test='project-response-select'
      className={cn(
        'flex flex-col items-center justify-center gap-4 rounded-2xl border border-b-2 border-gray-200 bg-white px-6 py-8',
        app &&
          'border border-(--brand-separation-border) bg-(--brand-assistant-message-bg) shadow-(--brand-separation-shadow) backdrop-blur-sm',
        className
      )}
    >
      {isAiVoiceEnabled && (
        <ProjectResponseMessageRendererAudio audio={audio ?? undefined} />
      )}

      {text && (
        <ProjectResponseQuestionText
          text={text}
          className={cn(app && 'text-(--brand-text-on-assistant-message)')}
        />
      )}

      {assets && assets?.length > 0 && assets[0]?.url && (
        <ProjectResponseAssetItem asset={assets[0]} />
      )}

      <div className='flex flex-col items-center justify-center gap-2 self-stretch'>
        {reorderedOptions.map((option, index) => {
          const isOptionSelected = isValueSelected(option.value);

          return (
            <ProjectResponseSelectItem
              key={option.value}
              app={app}
              value={option.value}
              tag={getAlphabetLabel(index)}
              selected={isOptionSelected}
              onClick={() => handleClick(option.value)}
              disabled={Boolean(readonly)}
              label={option.label}
            />
          );
        })}
      </div>
    </div>
  );
}
